import React from "react";
import color from "color";
import brands from "gui/theme/brands.json";

// A generic size class used for things like breakpoints
// and component width/height variant props.
export type Size = "xs" | "sm" | "md" | "lg" | "xl";

interface VisualTheme {
  axis_legend_color: string;
  axis_text_color: string;
  axis_zero_color: string;
  background_color: string;
  chart_colors?: string[];
  chart_title_color: string;
  created_by_color: string;
  description_color: string;
  grid_lines_color: string;
  series_legend_color: string;
  username_color: string;
}

// Create theme color CSS vars from a single base color.
export const createThemeColors = (hex: string) => {
  const c = color(hex);
  const w = color("#fff");
  const b = color("#000");

  const hslw = w.hsl().string();
  const hslb = b.hsl().string();
  const text = c.contrast(w) > 4 ? hslw : hslb;

  return {
    "--color1-100": hslw,
    "--color1-200": c.alpha(0.1).hsl().string(),
    "--color1-300": c.alpha(0.3).hsl().string(),
    "--color1-400": c.alpha(0.5).hsl().string(),
    "--color1-500": c.hsl().string(),
    "--color1-100-stroke": hslb,
    "--color1-200-stroke": hslb,
    "--color1-300-stroke": hslb,
    "--color1-400-stroke": hslb,
    "--color1-500-stroke": text,
    color: hslb,
  } as React.CSSProperties;
};

const defaultTheme: VisualTheme = brands["dune"];

// Find theme for a given brand name.
// Some brands have a randomized key to make discovery slightly less obvious.
export const getTheme = (brand: string) => {
  if (brand in brands) {
    return (brands as { [key: string]: VisualTheme })[brand];
  }
  return defaultTheme;
};

const ThemeContext: React.Context<VisualTheme> = React.createContext(
  defaultTheme
);

export const ThemeProvider: React.FC<{ theme: VisualTheme }> = (props) => {
  return (
    <ThemeContext.Provider value={props.theme}>
      {props.children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): VisualTheme => {
  return React.useContext(ThemeContext);
};

// Extra colors for charts, ordered by preference.
// https://carto.com/carto-colors
export const chartColors = [
  "#56569b",
  "#f4603e",
  "#909090",

  "#cc61b0",
  "#f2b701",
  "#5d69b1",
  "#52bca3",
  "#99c945",
  "#24796c",
  "#e68310",
  "#2f8ac4",
  "#764e9f",
  "#ed645a",
  "#cc3a8e",
  "#a5aa99",

  "#66c5cc",
  "#f6cf71",
  "#f89c74",
  "#dcb0f2",
  "#87c55f",
  "#9eb9f3",
  "#fe88b1",
  "#c9db74",
  "#8be0a4",
  "#b497e7",
  "#d3b484",
  "#b3b3b3",
];
